import type {
  IFilterOptions,
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import capitalize from "lodash/capitalize";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { useFiltersStorage } from "storage/filters";
import { getNewFilter } from "utils/filters";

const useAssignedVulnerabilitiesFilter = ({
  setFilteredData,
  vulnerabilities,
}: Readonly<{
  setFilteredData: React.Dispatch<React.SetStateAction<IVulnRowAttr[]>>;
  vulnerabilities: IVulnRowAttr[];
}>): IUseFilterProps<IVulnRowAttr> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const options: IOptionsProps<IVulnRowAttr>[] = useMemo(
    (): IOptionsProps<IVulnRowAttr>[] => [
      {
        filterOptions: [
          {
            key: "organizationName",
            label: "Organization",
            options: Array.from(
              new Set(
                vulnerabilities.map(
                  (vuln): string => vuln.organizationName ?? "",
                ),
              ),
            ).map(
              (organizationName): IOptions => ({
                label: capitalize(organizationName),
                value: capitalize(organizationName),
              }),
            ),
            type: "select",
          },
        ],
        label: "Organization",
      },
      {
        filterOptions: [
          {
            key: "groupName",
            label: t("organization.tabs.groups.newGroup.name.text"),
            options: Array.from(
              new Set(vulnerabilities.map((vuln): string => vuln.groupName)),
            ).map(
              (groupName): IOptions => ({
                label: capitalize(groupName),
                value: capitalize(groupName),
              }),
            ),
            type: "select",
          },
        ],
        label: t("organization.tabs.groups.newGroup.name.text"),
      },
      {
        filterOptions: [
          {
            key: "finding",
            label: t(
              "searchFindings.tabVuln.vulnTable.vulnerabilityType.title",
            ),
            options: Array.from(
              new Set(
                vulnerabilities.map(
                  (vuln): string => vuln.finding?.title ?? "",
                ),
              ),
            ).map((title): IOptions => ({ label: title, value: title })),
            type: "select",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.vulnerabilityType.title"),
      },
      {
        filterOptions: [
          {
            key: "verification",
            label: t("searchFindings.tabVuln.vulnTable.reattack"),
            options: [
              { label: t("searchFindings.tabVuln.onHold"), value: "On_hold" },
              {
                label: t("searchFindings.tabVuln.requested"),
                value: "Requested",
              },
              {
                label: t("searchFindings.tabVuln.verified"),
                value: "Verified",
              },
              {
                label: t("searchFindings.tabVuln.notRequested"),
                value: "NotRequested",
              },
            ],
            type: "select",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.reattack"),
      },
      {
        filterOptions: [
          {
            key: "tag",
            label: t("searchFindings.tabVuln.vulnTable.tags"),
            type: "text",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.tags"),
      },
    ],
    [t, vulnerabilities],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("assigned_vulnerabilities") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("assigned_vulnerabilities", value);
    },
    [setValues],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IVulnRowAttr>[]): void => {
      const newFiltersArray = newFilters as IFilterOptions<IVulnRowAttr>[];
      const title = getNewFilter(newFiltersArray, "finding")?.value;
      const tags = getNewFilter(newFiltersArray, "tag")?.value;
      const verification = getNewFilter(newFiltersArray, "verification")?.value;
      const group = getNewFilter(
        newFiltersArray,
        "groupName",
      )?.value?.toLowerCase();
      const organization = getNewFilter(
        newFiltersArray,
        "organizationName",
      )?.value?.toLowerCase();

      setFilteredData(
        vulnerabilities.filter(
          (vuln): boolean =>
            (title?.includes(vuln.finding?.title ?? "") ?? true) &&
            (organization?.includes(
              vuln.organizationName?.toLowerCase() ?? "",
            ) ??
              true) &&
            (group?.includes(vuln.groupName.toLowerCase()) ?? true) &&
            (verification?.startsWith(vuln.verification ?? "null") ?? true) &&
            vuln.tag.includes(tags ?? ""),
        ),
      );
    },
    [setFilteredData, vulnerabilities],
  );

  return useFilters({
    dataset: vulnerabilities,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setValues: handleSetValues,
  });
};

export { useAssignedVulnerabilitiesFilter };
